import { zodResolver } from '@hookform/resolvers/zod'
import {
  FormProvider,
  useForm,
  useFieldArray,
  Controller,
} from 'react-hook-form'
import { useEffect, useMemo, useState } from 'react'
import { AutoComplete } from 'qbs-core'

import { dietPlanFormSchema, DietPlanSchema } from './schema'
import { DialogModal, TextField } from '../../../../../components/common'
import FormBuilder from '../../../../../components/app/formBuilder'
import ToggleSwitch from '../../../../../components/common/inputs/ToggleSwitch'
import { useCreateDietPlan, useDietPlanDetail, useUpdateDietPlan } from '../api'
import { useMeals } from '../../../../Meals/api'
import {
  getMealTimingDetails,
  useMealTimingList,
} from '../../../../MealTiming/api'
import Button from '../../../../../components/common/buttons/Button'
import { useSnackbarManager } from '../../../../../components/common/snackbar'

const MEAL_TIME_OPTIONS = [
  { id: 'Morning drink', name: 'Morning drink', value: 'Morning drink' },
  { id: 'Breakfast', name: 'Breakfast', value: 'Breakfast' },
  { id: 'Mid day meal', name: 'Mid day meal', value: 'Mid day meal' },
  { id: 'Lunch', name: 'Lunch', value: 'Lunch' },
  { id: 'Evening snack', name: 'Evening snack', value: 'Evening snack' },
  { id: 'Dinner', name: 'Dinner', value: 'Dinner' },
  { id: 'Bed time', name: 'Bed time', value: 'Bed time' },
]

const toTitleCase = (value: any) => {
  if (typeof value !== 'string') return ''
  return value
    .toLowerCase()
    .replace(/\b([a-z])/g, (letter) => letter.toUpperCase())
}

const getDayKeys = (
  dayName?: string | null,
  dayNumber?: number | string | null
) => {
  const keys: string[] = []
  const num = Number(dayNumber)
  if (Number.isFinite(num) && num > 0) {
    keys.push(`number:${num}`)
    return keys
  }
  return keys
}

const normalizeMealTime = (value?: string | null) => {
  if (!value) return ''
  return value.toString().trim().toLowerCase()
}

type Props = {
  isOpen: boolean
  handleClose: () => void
  edit?: boolean
  rowData?: any
  planId?: string | number
  planDurationDays?: number
  existingPlans?: any[]
}

const DietPlanForm = ({
  isOpen,
  handleClose,
  edit,
  rowData,
  planId,
  planDurationDays,
  existingPlans = [],
}: Props) => {
  const { enqueueSnackbar } = useSnackbarManager()
  const [initialDayName, setInitialDayName] = useState('')
  const { data: detailData, refetch: refetchDetail } = useDietPlanDetail(
    edit ? rowData?.id : undefined
  )

  // Check if this is creating a meal for a specific day (prefilled day info)
  const isCreatingForSpecificDay = !edit && Boolean(rowData?.day_number)

  const durationDays =
    Number(planDurationDays ?? 0) ||
    Number((detailData as any)?.diet_plan_template?.duration_days ?? 0)
  const dayNumbers = useMemo(() => {
    if (Number(durationDays) > 0) {
      return Array.from({ length: Number(durationDays) }, (_, i) => i + 1)
    }
    return []
  }, [durationDays])

  const methods = useForm<DietPlanSchema>({
    resolver: zodResolver(dietPlanFormSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      diet_plan_template_id: Number(
        planId ?? rowData?.diet_plan_template_id ?? 0
      ),
      day_number: Number(rowData?.day_number ?? 0),
      sequence_number: Number(rowData?.sequence_number ?? 1),
      meal_time: rowData?.meal_time ?? '',
      meal_time_time: rowData?.meal_time_time ?? '',
      day_name:
        rowData?.day_name ??
        (rowData?.day_number ? `Day ${rowData.day_number}` : ''),
      notes: rowData?.notes ?? '',
      protein: (rowData as any)?.protein ?? '',
      carbs: (rowData as any)?.carbs ?? '',
      fat: (rowData as any)?.fat ?? '',
      fiber: (rowData as any)?.fiber ?? '',
      total_calories: (rowData as any)?.total_calories ?? '',
      calories: rowData?.calories ?? '',
    },
  })

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
    control,
    formState: { errors },
  } = methods
  const { mutate: createMutate, isLoading: creating } = useCreateDietPlan()
  const { mutate: updateMutate, isLoading: updating } = useUpdateDietPlan()

  const {
    fields: mealFields,
    append: appendMeal,
    remove: removeMeal,
    replace: replaceMeals,
  } = useFieldArray({
    control,
    name: 'meals',
  })

  const selectedMealTime = watch('meal_time')
  const selectedDayNumber = watch('day_number')

  const mealTimingParams = useMemo(
    () => ({
      page: 1,
      page_size: 1000,
      search: '',
      ordering: '',
      status: 'active',
    }),
    []
  )
  const { data: mealTimingListData } = useMealTimingList(
    mealTimingParams as any
  )

  const mealTimeOptions = useMemo(() => {
    const items = (mealTimingListData as any)?.meal_timings ?? []
    const fromApi = Array.isArray(items)
      ? items
          .map((mt: any) => {
            const rawName = mt?.name
            const id = mt?.id ?? rawName
            if (!rawName || typeof rawName !== 'string') return null
            const trimmed = rawName.trim()
            if (!trimmed) return null
            const formattedName =
              trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase()
            return { id, name: formattedName, value: rawName, time: mt?.time }
          })
          .filter(Boolean)
      : []

    return fromApi.length > 0 ? (fromApi as any[]) : MEAL_TIME_OPTIONS
  }, [mealTimingListData])
  const searchParams = {
    page: 1,
    per_page: 999,
    search: '',
    ordering: '',
    meal_time: selectedMealTime || undefined,
    status: 'active',
  }

  const { data: mealsData, refetch: refetchMeals } = useMeals(
    searchParams as any
  )
  const dayOptions = useMemo(() => {
    return dayNumbers.map((value) => ({
      id: String(value),
      name: `Day ${value}`,
      value: String(value),
    }))
  }, [dayNumbers])

  const normalizedExistingPlans = useMemo(() => {
    if (!Array.isArray(existingPlans)) return []
    if (edit && rowData?.id) {
      return existingPlans.filter((plan: any) => plan?.id !== rowData.id)
    }
    return existingPlans
  }, [existingPlans, edit, rowData?.id])

  const dayMealTimeMap = useMemo(() => {
    const map = new Map<string, Set<string>>()
    normalizedExistingPlans.forEach((plan: any) => {
      const normalizedMealTime = normalizeMealTime(plan?.meal_time)
      if (!normalizedMealTime) return
      const keys = getDayKeys(plan?.day_name, plan?.day_number)
      if (!keys.length) return
      keys.forEach((key) => {
        const existing = map.get(key)
        if (existing) {
          existing.add(normalizedMealTime)
          return
        }
        map.set(key, new Set([normalizedMealTime]))
      })
    })
    return map
  }, [normalizedExistingPlans])

  const usedMealTimeValues = useMemo(() => {
    const num = Number(selectedDayNumber)
    if (!Number.isFinite(num) || num <= 0) return new Set<string>()
    return dayMealTimeMap.get(`number:${num}`) || new Set<string>()
  }, [selectedDayNumber, dayMealTimeMap])

  const normalizedSelectedMealTime = normalizeMealTime(selectedMealTime)

  const availableMealTimeOptions = useMemo(() => {
    const formatSentenceCase = (str: any) => {
      if (!str || typeof str !== 'string') return ''
      const trimmed = str.trim()
      if (!trimmed) return ''
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase()
    }

    const filtered = !selectedDayNumber
      ? mealTimeOptions
      : mealTimeOptions.filter((option: any) => {
          const normalized = normalizeMealTime(option.value)
          if (
            normalizedSelectedMealTime &&
            normalized === normalizedSelectedMealTime
          )
            return true
          return !usedMealTimeValues.has(normalized)
        })

    return filtered.map((option: any) => ({
      ...option,
      name: formatSentenceCase(option.name),
    }))
  }, [
    selectedDayNumber,
    normalizedSelectedMealTime,
    usedMealTimeValues,
    mealTimeOptions,
  ])

  const selectedMealTimeTime = useMemo(() => {
    if (!selectedMealTime) return ''
    const match = mealTimeOptions.find(
      (mt: any) =>
        String(mt?.id) === String(selectedMealTime) ||
        String(mt?.value) === String(selectedMealTime) ||
        String(mt?.name) === String(selectedMealTime)
    )
    return match?.time ? String(match.time) : ''
  }, [mealTimeOptions, selectedMealTime])

  const isMealTimeRestricted =
    !!normalizedSelectedMealTime &&
    usedMealTimeValues.has(normalizedSelectedMealTime)

  useEffect(() => {
    if (edit) return
    if (!isMealTimeRestricted) return
    setValue('meal_time', '', { shouldValidate: true })
  }, [edit, isMealTimeRestricted, setValue])

  useEffect(() => {
    if (!isOpen) return
    const currentMealTimeTime = watch('meal_time_time')
    if (currentMealTimeTime) return

    const nextValue =
      rowData?.meal_time_time ??
      (selectedMealTime ? selectedMealTimeTime : '') ??
      ''

    if (!nextValue) return

    setValue('meal_time_time', nextValue, { shouldValidate: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    rowData?.meal_time_time,
    selectedMealTime,
    selectedMealTimeTime,
    setValue,
  ])

  useEffect(() => {
    if (!isOpen) return
    if (!selectedMealTime) return
    const currentMealTimeTime = watch('meal_time_time')
    if (currentMealTimeTime) return
    if (selectedMealTimeTime) return

    const selectedId = Number(selectedMealTime)
    if (!Number.isFinite(selectedId) || selectedId <= 0) return

    let cancelled = false
    ;(async () => {
      try {
        const res: any = await getMealTimingDetails(String(selectedId))
        if (cancelled) return
        const time = res?.meal_timing?.time
        if (time) {
          setValue('meal_time_time', String(time), { shouldValidate: false })
        }
      } catch {
        // ignore (fallback is empty)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isOpen, selectedMealTime, selectedMealTimeTime, setValue, watch])

  const allMeals = (mealsData as any)?.meals ?? []
  const filteredMeals = allMeals
  const mealsFormValues = (watch('meals') as any[]) ?? []
  const totalCaloriesFromMeals = mealsFormValues.reduce((sum, m) => {
    const meal = filteredMeals.find(
      (fm: any) => String(fm.id) === String(m.meal_id)
    )
    const count = Number(m?.count || 1)
    if (!meal) return sum

    const perServingProtein = Number(
      meal?.per_serving?.protein ?? meal?.calories_breakdown?.protein ?? 0
    )
    const perServingCarbs = Number(
      meal?.per_serving?.carbs ?? meal?.calories_breakdown?.carbs ?? 0
    )
    const perServingFat = Number(
      meal?.per_serving?.fat ?? meal?.calories_breakdown?.fat ?? 0
    )
    const perServingFiber = Number(
      meal?.per_serving?.fiber ?? meal?.calories_breakdown?.fiber ?? 0
    )
    const perServingTotal =
      perServingProtein + perServingCarbs + perServingFat + perServingFiber

    return sum + perServingTotal * count
  }, 0)

  useEffect(() => {
    if (!selectedMealTime || edit) return

    const mapping: Record<string, number> = {
      'Morning drink': 1,
      Breakfast: 2,
      'Mid day meal': 3,
      Lunch: 4,
      'Evening snack': 5,
      Dinner: 6,
      'Bed time': 7,
    }

    const seq = mapping[selectedMealTime as keyof typeof mapping]
    if (seq != null) {
      setValue('sequence_number', seq, { shouldValidate: true })
    }
  }, [selectedMealTime, edit, setValue])

  useEffect(() => {
    if (!edit) return
    if (!isOpen) return

    refetchMeals()
  }, [edit, isOpen, selectedMealTime, refetchMeals])

  useEffect(() => {
    if (!isOpen) return

    // In edit mode, always refetch the detail data to get the latest updates
    if (edit && rowData?.id) {
      console.log('Refetching detail data on modal open...')
      refetchDetail()
    }

    const source: any =
      edit && detailData
        ? ((detailData as any).diet_plan ?? detailData)
        : rowData || {}

    console.log(
      'Form opening - edit:',
      edit,
      'detailData:',
      detailData,
      'rowData:',
      rowData,
      'source:',
      source
    )

    // Store initial day name for resetting after "Add New"
    const dayName =
      source?.day_name || (source?.day_number ? `Day ${source.day_number}` : '')
    setInitialDayName(dayName)

    const itemsSource =
      edit && Array.isArray((detailData as any)?.meals)
        ? (detailData as any).meals
        : Array.isArray(source?.items)
          ? source.items
          : []

    const mappedMeals =
      itemsSource.length > 0
        ? itemsSource.map((it: any) => {
            const rawReq = it.requirement ?? it.key_requirement
            const normalizedReq =
              String(rawReq ?? 'Optional').toLowerCase() === 'mandatory'
                ? 'Mandatory'
                : 'Optional'
            return {
              meal_id: it.meal_id ?? it.id ?? 0,
              count: it.quantity ?? it.default_serving_quantity ?? 1,
              requirement: normalizedReq,
              protein: '',
              carbs: '',
              fat: '',
              fiber: '',
              total_calories: '',
            }
          })
        : []

    reset({
      diet_plan_template_id: Number(
        planId ?? source?.diet_plan_template_id ?? 0
      ),
      day_number: Number(source?.day_number ?? rowData?.day_number ?? 0),
      sequence_number: edit ? Number(source?.sequence_number ?? 1) : 0,
      meal_time: source?.meal_time ?? '',
      meal_name: source?.meal_name ?? '',
      day_name:
        source?.day_name ||
        (source?.day_number ? `Day ${source.day_number}` : ''),
      notes: source?.notes ?? '',
      protein: (source as any)?.protein ?? '',
      carbs: (source as any)?.carbs ?? '',
      fat: (source as any)?.fat ?? '',
      fiber: (source as any)?.fiber ?? '',
      total_calories: (source as any)?.total_calories ?? '',
      calories: source?.calories ?? '',
      meals:
        mappedMeals.length > 0
          ? mappedMeals
          : [
              {
                meal_id: 0,
                count: 1,
                requirement: 'Optional',
                protein: '',
                carbs: '',
                fat: '',
                fiber: '',
                total_calories: '',
              },
            ],
    } as any)

    if (mappedMeals.length > 0) {
      replaceMeals(mappedMeals)
    } else {
      replaceMeals([
        {
          meal_id: 0,
          count: 1,
          requirement: 'Optional',
          protein: '',
          carbs: '',
          fat: '',
          fiber: '',
          total_calories: '',
        },
      ])
    }
  }, [
    isOpen,
    planId,
    edit,
    rowData,
    detailData,
    reset,
    replaceMeals,
    refetchDetail,
  ])

  const onSubmit = (values: DietPlanSchema, keepOpen = false) => {
    // Check for empty meal items and show snackbar error if found
    const hasEmptyMeals = (values.meals || []).some(
      (meal) =>
        (!Number.isFinite(meal.meal_id) || meal.meal_id === 0) &&
        (!Number.isFinite(meal.count) || meal.count === 0)
    )

    if (hasEmptyMeals) {
      enqueueSnackbar(
        'Please fill all required fields for each meal or remove empty meals',
        {
          variant: 'error',
        }
      )
      return
    }

    // Check if there are any valid meals (both meal_id and count > 0)
    const hasValidMeals = (values.meals || []).some(
      (meal) =>
        Number.isFinite(meal.meal_id) &&
        meal.meal_id > 0 &&
        Number.isFinite(meal.count) &&
        meal.count > 0
    )

    if (!hasValidMeals) {
      enqueueSnackbar(
        'Please fill all required fields for each meal or remove empty meals',
        {
          variant: 'error',
        }
      )
      return
    }

    const itemsPayload = (values.meals || [])
      .filter((m: any) => typeof m.meal_id === 'number' && m.meal_id && m.count)
      .map((m: any) => ({
        meal_id: m.meal_id as number,
        quantity: Number(m.count),
        requirement: (m.requirement === 'Mandatory'
          ? 'mandatory'
          : 'optional') as 'mandatory' | 'optional',
      }))

    const payload: any = {
      diet_plan: {
        diet_plan_template_id: Number(values.diet_plan_template_id ?? planId),
        day_number: Number(values.day_number),
        day_name: values.day_name || '',
        sequence_number: Number(values.sequence_number),
        meal_time: values.meal_time,
        calories: values.calories === '' ? null : Number(values.calories),
        notes: values.notes || '',
      },
    }

    if (itemsPayload.length > 0) {
      payload.items = itemsPayload
    }
    if (edit && rowData?.id) {
      updateMutate(
        { id: rowData.id, payload },
        {
          onSuccess: async () => {
            console.log('Update success, refetching detail data...')
            enqueueSnackbar('Diet plan updated successfully', {
              variant: 'success',
            })
            await refetchDetail()
            console.log('Detail data refetched')
            handleClose()
          },
        }
      )
    } else {
      createMutate(payload, {
        onSuccess: () => {
          enqueueSnackbar('Diet plan created successfully', {
            variant: 'success',
          })
          if (keepOpen) {
            // Keep the form open and reset for new entry with same day
            const currentDayNumber = values.day_number
            const currentDayName = values.day_name || initialDayName
            reset({
              diet_plan_template_id: Number(
                planId ?? values.diet_plan_template_id ?? 0
              ),
              day_number: currentDayNumber,
              day_name: currentDayName,
              sequence_number: 0,
              meal_time: '',
              meal_name: '',
              notes: '',
              protein: '',
              carbs: '',
              fat: '',
              fiber: '',
              total_calories: '',
              calories: '',
              meals: [
                {
                  meal_id: 0,
                  count: 1,
                  requirement: 'Optional',
                  protein: '',
                  carbs: '',
                  fat: '',
                  fiber: '',
                  total_calories: '',
                },
              ],
            } as any)
            replaceMeals([
              {
                meal_id: 0,
                count: 1,
                requirement: 'Optional',
                protein: '',
                carbs: '',
                fat: '',
                fiber: '',
                total_calories: '',
              },
            ])
          } else {
            handleClose()
          }
        },
      })
    }
  }

  const getFirstFormErrorMessage = (errs: any): string | null => {
    if (!errs) return null
    if (typeof errs === 'string') return errs

    if (Array.isArray(errs)) {
      for (const item of errs) {
        const msg = getFirstFormErrorMessage(item)
        if (msg) return msg
      }
      return null
    }

    if (typeof errs === 'object') {
      if (errs?.message) return String(errs.message)

      for (const key of Object.keys(errs)) {
        const msg = getFirstFormErrorMessage(errs[key])
        if (msg) return msg
      }
    }

    return null
  }

  const onInvalidSubmit = (formErrors: any) => {
    const msg =
      getFirstFormErrorMessage(formErrors) ||
      'Please fix the highlighted errors and try again'
    enqueueSnackbar(msg, { variant: 'error' })
  }

  const submitAndClose = handleSubmit(
    (values) => onSubmit(values, false),
    onInvalidSubmit
  )
  const submitAndAddNew = handleSubmit(
    (values) => onSubmit(values, true),
    onInvalidSubmit
  )

  const handleMealTimeChange = (option: any) => {
    const mealTime = option?.id ?? option?.value ?? ''
    setValue('meal_time', mealTime, { shouldValidate: true })

    const timeFromOption = option?.time
    if (typeof timeFromOption === 'string') {
      setValue('meal_time_time', timeFromOption, { shouldValidate: false })
    } else if (mealTime) {
      const match = mealTimeOptions.find(
        (mt: any) =>
          String(mt?.id) === String(mealTime) ||
          String(mt?.value) === String(mealTime) ||
          String(mt?.name) === String(mealTime)
      )
      if (match?.time) {
        setValue('meal_time_time', String(match.time), {
          shouldValidate: false,
        })
      }
    }

    if (mealTime) {
      const mapping: Record<string, number> = {
        'Morning drink': 1,
        Breakfast: 2,
        'Mid day meal': 3,
        Lunch: 4,
        'Evening snack': 5,
        Dinner: 6,
        'Bed time': 7,
      }

      const seq = mapping[mealTime as keyof typeof mapping]
      if (seq != null) {
        setValue('sequence_number', seq, { shouldValidate: true })
      }
    }
  }

  const formFields = [
    {
      name: 'meal_time',
      label: 'Meal ',
      type: 'custom_search_select',
      desc: 'name',
      descId: 'id',
      id: 'meal_time_id',
      placeholder: 'Select meal time',
      initialLoad: rowData?.meal_time ?? '',
      data: availableMealTimeOptions,
      required: true,
      disabled: !!edit,
      onChange: handleMealTimeChange,
    },
    {
      name: 'meal_time_time',
      label: 'Meal Time',
      type: 'text',
      placeholder: '',
      initialLoad: rowData?.meal_time_time ?? selectedMealTimeTime ?? '',
      required: false,
      disabled: true,
    },

    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      placeholder: 'Add notes (optional)',
      required: false,
    },
  ]

  const showMealsSection = edit || !!selectedMealTime

  return (
    <DialogModal
      isOpen={isOpen}
      onClose={handleClose}
      title={edit ? 'Edit Diet Plan' : 'Create Diet Plan'}
      actionLabel={edit ? 'Update' : 'Create'}
      actionLoader={creating || updating}
      onSubmit={submitAndClose}
      secondaryAction={handleClose}
      secondaryActionLabel="Cancel"
      small={false}
      actionBody={
        <div className="flex flex-row gap-2 w-full justify-end">
          <Button
            disabled={creating || updating}
            label="Cancel"
            onClick={handleClose}
            outlined
          />
          {!edit && (
            <Button
              disabled={creating || updating}
              isLoading={creating}
              label="Submit and Add New"
              onClick={submitAndAddNew}
              className="bg-blue-500"
            />
          )}
          {edit && (
            <Button
              disabled={creating || updating}
              isLoading={creating || updating}
              label="Update"
              onClick={submitAndClose}
              primary
            />
          )}
        </div>
      }
      body={
        <div className="max-h-[70vh] min-h-[250px] pr-1">
          <FormProvider {...methods}>
            <>
              <div className="mb-3">
                <label className="block text-[12px] text-grey-medium mb-1">
                  Day <span className="text-error">*</span>
                </label>
                <Controller
                  name="day_number"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <AutoComplete
                      name="day_number"
                      type="custom_search_select"
                      desc="name"
                      descId="id"
                      placeholder="Select day"
                      data={dayOptions}
                      value={
                        value
                          ? dayOptions.find((d) => d.value === String(value))
                              ?.name || `Day ${value}`
                          : ''
                      }
                      disabled={!!edit || isCreatingForSpecificDay}
                      className="w-full"
                      onChange={(option: any) => {
                        const raw = option?.value ?? option?.id ?? option?.name
                        const numeric = Number(raw)
                        const nextNum =
                          Number.isFinite(numeric) && numeric > 0 ? numeric : 0
                        onChange(nextNum)
                        setValue('day_name', nextNum ? `Day ${nextNum}` : '', {
                          shouldValidate: false,
                        })
                      }}
                    />
                  )}
                />
              </div>

              <FormBuilder data={formFields} edit={true} spacing />
              {showMealsSection && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold">Meals</h3>
                  </div>

                  {mealFields.map((field, index) => {
                    const selectedId = watch(`meals.${index}.meal_id` as const)
                    const selectedMeal = filteredMeals.find(
                      (m: any) => String(m.id) === String(selectedId)
                    )
                    const intakeQty = Number(
                      watch(`meals.${index}.count` as const) || 1
                    )
                    const mealErrors = (errors?.meals as any)?.[index]
                    const countError = mealErrors?.count
                    const mealIdError = mealErrors?.meal_id
                    const selectedIdsAll: number[] = (mealsFormValues || [])
                      .map((m: any) => Number(m?.meal_id || 0))
                      .filter((v: number) => Number.isFinite(v) && v > 0)
                    const selectedIdsExceptCurrent = selectedIdsAll.filter(
                      (id) => String(id) !== String(selectedId ?? '')
                    )
                    let availableMealsForRow = filteredMeals.filter(
                      (m: any) =>
                        !selectedIdsExceptCurrent.includes(Number(m?.id || 0))
                    )
                    if (
                      selectedMeal &&
                      !availableMealsForRow.some(
                        (m: any) => String(m.id) === String(selectedMeal.id)
                      )
                    ) {
                      availableMealsForRow = [
                        selectedMeal,
                        ...availableMealsForRow,
                      ]
                    }

                    const mealOptionsForRow = availableMealsForRow.map(
                      (meal: any) => ({
                        ...meal,
                        name: toTitleCase(meal?.name),
                      })
                    )

                    return (
                      <div
                        key={field.id}
                        className="mb-3 rounded border p-2 text-[11px]"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">Meal {index + 1}</span>
                        </div>

                        <div className="mb-2 flex items-center justify-end">
                          <Controller
                            name={`meals.${index}.requirement` as const}
                            control={control}
                            render={({ field: { value, onChange } }) => (
                              <div className="flex items-center gap-2">
                                <ToggleSwitch
                                  id={`meals-${index}-requirement`}
                                  checked={value === 'Mandatory'}
                                  onChange={(checked: boolean) =>
                                    onChange(checked ? 'Mandatory' : 'Optional')
                                  }
                                />
                                <span className="text-xxs">
                                  {value === 'Mandatory'
                                    ? 'Mandatory'
                                    : 'Optional'}
                                </span>
                              </div>
                            )}
                          />
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                          <div className="col-span-2 md:col-span-1">
                            <label className="block text-[10px] font-medium mb-1">
                              Meal Name <span className="text-error">*</span>
                            </label>
                            <AutoComplete
                              name={`meals.${index}.meal_id`}
                              type="custom_search_select"
                              desc="name"
                              descId="id"
                              required
                              placeholder="Select meal"
                              data={mealOptionsForRow}
                              value={
                                selectedMeal
                                  ? toTitleCase(selectedMeal.name)
                                  : ''
                              }
                              className="w-full"
                              onChange={(option: any) => {
                                const mealId = option?.id ?? option?.value ?? ''
                                setValue(
                                  `meals.${index}.meal_id` as const,
                                  mealId === '' ? 0 : Number(mealId),
                                  { shouldValidate: true }
                                )
                                trigger(`meals.${index}.meal_id`)
                                refetchMeals()
                              }}
                            />
                            {mealIdError && (
                              <div className="text-xs text-red-500 mt-1">
                                {mealIdError?.message as string}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-[10px] font-medium mb-1">
                              Intake quantity{' '}
                              <span className="text-error">*</span>
                            </label>
                            <Controller
                              name={`meals.${index}.count` as const}
                              control={control}
                              render={({ field: { onChange, value } }) => (
                                <TextField
                                  id={`meals.${index}.count`}
                                  name={`meals.${index}.count`}
                                  maxLength={3}
                                  type="text"
                                  placeholder=""
                                  value={
                                    value === undefined ||
                                    value === null ||
                                    value === 0
                                      ? ''
                                      : String(value)
                                  }
                                  onChange={(e: any) => {
                                    const v = e.target.value
                                    onChange(v === '' ? 0 : Number(v))
                                    // Trigger validation for both meal_id and count to show errors
                                    trigger(`meals.${index}.count`)
                                    trigger(`meals.${index}.meal_id`)
                                  }}
                                  allowPositiveOnly
                                  errors={
                                    countError
                                      ? {
                                          [`meals.${index}.count`]: countError,
                                        }
                                      : undefined
                                  }
                                />
                              )}
                            />
                          </div>

                          <div>
                            <label className="block text-[10px] font-medium mb-1">
                              Serving unit<span className="text-error">*</span>
                            </label>
                            <TextField
                              id={`meals.${index}.serving_unit`}
                              name={`meals.${index}.serving_unit`}
                              type="text"
                              placeholder=""
                              value={selectedMeal?.serving_unit ?? ''}
                              disabled
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium mb-1">
                              Total Calorie<span className="text-error">*</span>
                            </label>
                            <TextField
                              id={`meals.${index}.total_calories`}
                              name={`meals.${index}.total_calories`}
                              type="text"
                              placeholder=""
                              value={
                                !selectedMeal
                                  ? ''
                                  : String(
                                      Number(
                                        selectedMeal?.per_serving?.calories ??
                                          selectedMeal?.total_calories ??
                                          selectedMeal?.calories_breakdown
                                            ?.calories ??
                                          0
                                      )
                                    )
                              }
                              disabled
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <div>
                            <label className="block text-[10px] font-medium mb-1">
                              Protein<span className="text-error">*</span>
                            </label>
                            <TextField
                              id={`meals.${index}.protein`}
                              name={`meals.${index}.protein`}
                              type="text"
                              placeholder=""
                              value={
                                selectedMeal
                                  ? String(
                                      (selectedMeal?.per_serving?.protein ??
                                        selectedMeal?.calories_breakdown
                                          ?.protein ??
                                        0) * intakeQty
                                    )
                                  : ''
                              }
                              disabled
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium mb-1">
                              Carbs<span className="text-error">*</span>
                            </label>
                            <TextField
                              id={`meals.${index}.carbs`}
                              name={`meals.${index}.carbs`}
                              type="text"
                              placeholder=""
                              value={
                                selectedMeal
                                  ? String(
                                      (selectedMeal?.per_serving?.carbs ??
                                        selectedMeal?.calories_breakdown
                                          ?.carbs ??
                                        0) * intakeQty
                                    )
                                  : ''
                              }
                              disabled
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium mb-1">
                              Fat<span className="text-error">*</span>
                            </label>
                            <TextField
                              id={`meals.${index}.fat`}
                              name={`meals.${index}.fat`}
                              type="text"
                              placeholder=""
                              value={
                                selectedMeal
                                  ? String(
                                      (selectedMeal?.per_serving?.fat ??
                                        selectedMeal?.calories_breakdown?.fat ??
                                        0) * intakeQty
                                    )
                                  : ''
                              }
                              disabled
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium mb-1">
                              Fiber<span className="text-error">*</span>
                            </label>
                            <TextField
                              id={`meals.${index}.fiber`}
                              name={`meals.${index}.fiber`}
                              type="text"
                              placeholder=""
                              value={
                                selectedMeal
                                  ? String(
                                      (selectedMeal?.per_serving?.fiber ??
                                        selectedMeal?.calories_breakdown
                                          ?.fiber ??
                                        0) * intakeQty
                                    )
                                  : ''
                              }
                              disabled
                            />
                          </div>
                        </div>

                        {mealFields.length > 1 && index > 0 && (
                          <div className="mb-2 mt-2 flex items-center justify-end">
                            <button
                              type="button"
                              className="text-[10px] text-red-500"
                              onClick={() => removeMeal(index)}
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  })}

                  <div className="mt-2 flex justify-between items-center">
                    <span className="text-[11px] font-semibold">
                      Total Calories: {totalCaloriesFromMeals || 0}
                    </span>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-full bg-blue-500 px-2.5 py-1 mb-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                      onClick={() =>
                        appendMeal({
                          meal_id: 0,
                          count: 1,
                          requirement: 'Optional',
                          protein: '',
                          carbs: '',
                          fat: '',
                          fiber: '',
                          total_calories: '',
                        })
                      }
                      aria-label="Add meal row"
                    >
                      +
                    </button>
                  </div>
                </div>
              )}
            </>
          </FormProvider>
        </div>
      }
    />
  )
}

export default DietPlanForm
